import { useEffect, useRef } from 'react'

const FOCUSABLE = 'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'

// A modal's focus contract: focus moves inside on open (the first control, or the box itself with
// `focusBox`), Tab and Shift+Tab stay inside, Escape calls `onEscape`, and focus returns to the
// control that opened it on close. aria-modal alone confines none of this.
export const useDialogFocus = (boxRef, onEscape, { focusBox = false } = {}) => {
  // Captured during render, before the dialog takes focus, so it is the control that opened it.
  const triggerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null)

  // Apart from the key handler, so a re-render while open cannot pull focus back to the top.
  useEffect(() => {
    const trigger = triggerRef.current
    const target = focusBox ? boxRef.current : boxRef.current?.querySelector(FOCUSABLE)
    target?.focus?.()
    return () => { trigger?.focus?.() }
  }, [])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onEscape()
        return
      }
      if (event.key !== 'Tab' || !boxRef.current) { return }

      const items = [ ...boxRef.current.querySelectorAll(FOCUSABLE) ].filter((el) => !el.disabled)
      if (items.length === 0) { return }
      const first = items[0]
      const last = items[items.length - 1]
      // Shift+Tab off the first control, or off anything that is not one (the box itself), wraps.
      if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ onEscape ])
}
